import React, { useState, useRef, useEffect } from 'react';
import { CreateTimestampedNoteRequest } from '../../types/artist';
import { FormGroup, FormLabel, FormTextarea } from './FormComponents';
import './FeedbackForm.css';

export interface FeedbackFormProps {
  trackTitle: string;
  // Rating state
  overallRating: number;
  setOverallRating: (rating: number) => void;
  compositionRating: number;
  setCompositionRating: (rating: number) => void;
  musicQualityRating: number;
  setMusicQualityRating: (rating: number) => void;
  productionQualityRating: number;
  setProductionQualityRating: (rating: number) => void;
  
  // Review text state
  review: string;
  setReview: (review: string) => void;
  
  // Timestamped notes state
  timestampedNotes: CreateTimestampedNoteRequest[];
  currentNote: string;
  setCurrentNote: (note: string) => void;
  addTimestampedNote: () => void;
  removeTimestampedNote: (index: number) => void;
  
  // Current time for timestamped notes
  currentTime: number;
  formatTime: (seconds: number) => string;
  
  // Form submission
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  submittingFeedback: boolean;
  
  // Button text customization
  cancelButtonText?: string;
  submitButtonText?: string;
  
  // Optional report callback
  onReport?: () => void;
}

export const FeedbackForm: React.FC<FeedbackFormProps> = ({
  trackTitle,
  overallRating,
  setOverallRating,
  compositionRating,
  setCompositionRating,
  musicQualityRating,
  setMusicQualityRating,
  productionQualityRating,
  setProductionQualityRating,
  review,
  setReview,
  timestampedNotes,
  currentNote,
  setCurrentNote,
  addTimestampedNote,
  removeTimestampedNote,
  currentTime,
  formatTime,
  onSubmit,
  onCancel,
  submittingFeedback,
  cancelButtonText = "Cancel",
  submitButtonText = "Submit Feedback",
  onReport
}) => {
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMoreMenu(false);
      }
    };

    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreMenu]);

  const handleReportClick = () => {
    setShowMoreMenu(false);
    onReport?.();
  };

  return (
    <div className="feedback-form-section">
      <div className="feedback-form-header">
        <h3>Provide Feedback for "{trackTitle}"</h3>
        {onReport && (
          <div className="more-menu-container" ref={menuRef}>
            <button
              type="button"
              className="more-menu-button"
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              aria-label="More options"
            >
              ⋯
            </button>
            {showMoreMenu && (
              <div className="more-menu-dropdown">
                <button
                  type="button"
                  className="more-menu-item report"
                  onClick={handleReportClick}
                >
                  🚩 Report
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <form onSubmit={onSubmit} className="feedback-form">
        {/* Rating Inputs */}
        <div className="ratings-grid">
          <div className="rating-group">
            <label htmlFor="overall-rating">Overall Rating</label>
            <div className="rating-input">
              <input
                id="overall-rating"
                type="range"
                min="1"
                max="10"
                value={overallRating}
                onChange={(e) => setOverallRating(parseInt(e.target.value))}
              />
              <span className="rating-value">{overallRating}/10</span>
            </div>
          </div>

          <div className="rating-group">
            <label htmlFor="composition-rating">Composition</label>
            <div className="rating-input">
              <input
                id="composition-rating"
                type="range"
                min="1"
                max="10"
                value={compositionRating}
                onChange={(e) => setCompositionRating(parseInt(e.target.value))}
              />
              <span className="rating-value">{compositionRating}/10</span>
            </div>
          </div>

          <div className="rating-group">
            <label htmlFor="music-quality-rating">Music Quality</label>
            <div className="rating-input">
              <input
                id="music-quality-rating"
                type="range"
                min="1"
                max="10"
                value={musicQualityRating}
                onChange={(e) => setMusicQualityRating(parseInt(e.target.value))}
              />
              <span className="rating-value">{musicQualityRating}/10</span>
            </div>
          </div>

          <div className="rating-group">
            <label htmlFor="production-rating">Production Quality</label>
            <div className="rating-input">
              <input
                id="production-rating"
                type="range"
                min="1"
                max="10"
                value={productionQualityRating}
                onChange={(e) => setProductionQualityRating(parseInt(e.target.value))}
              />
              <span className="rating-value">{productionQualityRating}/10</span>
            </div>
          </div>
        </div>

        {/* Review Text */}
        <FormGroup>
          <FormLabel htmlFor="review">Written Review</FormLabel>
          <FormTextarea
            id="review"
            value={review}
            onChange={(e) => setReview(e.target.value)}
            placeholder="Share your thoughts about this track... (100-2000 characters required)"
            maxLength={2000}
            rows={4}
            required
          />
          <div className="character-count" style={{ color: review.length < 100 ? '#d32f2f' : '#666' }}>
            {review.length}/100 minimum ({review.length}/2000 total)
            {review.length < 100 && <span> - Please write at least {100 - review.length} more character{100 - review.length !== 1 ? 's' : ''}</span>}
          </div>
        </FormGroup>

        {/* Timestamped Notes */}
        <div className="timestamped-notes-section">
          <h4>Timestamped Notes (Optional)</h4>
          <div className="add-note">
            <div className="note-input-group">
              <span className="current-time">At {formatTime(currentTime)}:</span>
              <input
                type="text"
                value={currentNote}
                onChange={(e) => setCurrentNote(e.target.value)}
                placeholder="Add a note at the current time..."
                maxLength={1000}
                onKeyPress={(e) => e.key === 'Enter' && addTimestampedNote()}
              />
              <button
                type="button"
                onClick={addTimestampedNote}
                disabled={!currentNote.trim()}
              >
                Add Note
              </button>
            </div>
          </div>

          {timestampedNotes.length > 0 && (
            <div className="notes-list">
              <h5>Your Notes:</h5>
              {timestampedNotes.map((note, index) => (
                <div key={index} className="note-item">
                  <span className="note-timestamp">
                    {formatTime(note.timestampInSeconds)}
                  </span>
                  <span className="note-text">{note.note}</span>
                  <button
                    type="button"
                    className="remove-note"
                    onClick={() => removeTimestampedNote(index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Buttons */}
        <div className="form-actions">
          <button
            type="button"
            className="cancel-button"
            onClick={onCancel}
            disabled={submittingFeedback}
          >
            {cancelButtonText}
          </button>
          <button
            type="submit"
            className="submit-button"
            disabled={submittingFeedback || review.length < 100}
          >
            {submittingFeedback ? 'Submitting...' : submitButtonText}
          </button>
        </div>
      </form>
    </div>
  );
};